import { useCallback, useEffect, useMemo, useState } from 'react'
import ZonesPanel from '../components/ZonesPanel'
import EditableCell from '../components/EditableCell'
import { showToast } from '../components/Toast'
import {
  deleteItem,
  deleteService,
  insertItem,
  insertService,
  listItemsAll,
  listServicesAll,
  patchItem,
  patchService,
  setItemServiceLink,
} from '../lib/catalog'

const PRICING_MODES = ['per_pound', 'per_item', 'subscription']
const ITEM_KINDS = ['addon', 'garment']
const ITEM_UNITS = ['per_item', 'per_order', 'per_bag', 'per_lb']

// Admin catalog — two stacked tables (Services + Items). Same inline-edit
// UX as All orders: click cell, Enter/blur to save, Escape to cancel, ✓
// flash, toast on error.

export default function AdminCatalog() {
  const [services, setServices] = useState([])
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const refresh = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const [s, i] = await Promise.all([listServicesAll(), listItemsAll()])
      setServices(s); setItems(i)
    } catch (err) { setError(err?.message || 'Load failed') }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { refresh() }, [refresh])

  if (loading) return <div className="rounded-xl bg-white p-6 text-center text-[13px] text-ink-2">Loading…</div>
  if (error) return <div className="rounded-xl border border-red bg-red/10 p-4 text-[12px] font-bold text-red">{error}</div>

  return (
    <div className="space-y-6">
      <ServicesTable services={services} onChanged={refresh} />
      <ZonesPanel items={items} />
      <ItemsTable items={items} services={services} onChanged={refresh} />
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════
// Services
// ══════════════════════════════════════════════════════════════════════

function ServicesTable({ services, onChanged }) {
  async function addService() {
    try { await insertService(); onChanged() }
    catch (err) { showToast(err?.message || 'Add failed') }
  }
  return (
    <section className="rounded-xl border border-line bg-white">
      <header className="flex items-center justify-between border-b border-line px-4 py-3">
        <h2 className="font-bricolage text-[15px] font-extrabold text-navy">Services</h2>
        <button
          type="button"
          onClick={addService}
          className="rounded-lg bg-navy px-3 py-1.5 text-[11px] font-extrabold text-white"
        >
          + Add service
        </button>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead className="bg-[#F9FAFB] text-ink-2">
            <tr>
              <Th>Name</Th>
              <Th>Mode</Th>
              <Th>Flat price</Th>
              <Th>Included lbs</Th>
              <Th>Extra per lb</Th>
              <Th>Turnaround</Th>
              <Th align="center">Active</Th>
              <Th align="center"></Th>
            </tr>
          </thead>
          <tbody>
            {services.map((s) => (
              <ServiceRow key={s.id} row={s} onChanged={onChanged} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function ServiceRow({ row, onChanged }) {
  async function patch(patch) {
    try { await patchService(row.id, patch); onChanged() }
    catch (err) { showToast(err?.message || 'Save failed'); throw err }
  }
  const mode = row.pricing_mode
  const isPerItem = mode === 'per_item'
  const isSub = mode === 'subscription'
  const includedLabel = isSub ? 'Monthly lbs' : 'Included lbs'
  const includedField = isSub ? 'monthly_included_lbs' : 'included_lbs'

  async function handleDelete() {
    const msg =
      `Delete service "${row.name}"?\n\n` +
      `This can't be undone. If you just want to hide it from booking, ` +
      `use the Active toggle instead — that's reversible.`
    if (!window.confirm(msg)) return
    try {
      const res = await deleteService(row.id)
      if (res.blocked) {
        showToast(
          `This service is used by ${res.count} orders and can't be deleted. ` +
          `Turn it off with the Active toggle instead — it will disappear from booking but stay in your history.`,
        )
        return
      }
      onChanged()
      showToast('Service deleted')
    } catch (err) {
      showToast(err?.message || 'Delete failed')
    }
  }

  return (
    <tr className="border-t border-line align-top">
      <td className="px-3 py-2 font-bold text-navy">
        <EditableCell value={row.name || ''} onCommit={(v) => patch({ name: v })} onError={(err) => showToast(err?.message || 'Save failed')} />
        <div className="mt-0.5 text-[10px] text-ink-3">key: {row.key}</div>
      </td>
      <td className="px-3 py-2">
        <select
          value={mode}
          onChange={(e) => patch({ pricing_mode: e.target.value })}
          className="rounded border border-line bg-white px-1.5 py-1 text-[12px]"
        >
          {PRICING_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </td>
      <td className="px-3 py-2 text-right">
        {isPerItem ? <span className="text-ink-3">—</span> : (
          <EditableCell
            value={row.base_price_cents != null ? (row.base_price_cents / 100).toFixed(2) : ''}
            display={row.base_price_cents != null ? `$${(row.base_price_cents / 100).toFixed(2)}` : <span className="text-ink-3">—</span>}
            type="number"
            align="right"
            formatOnSave={(v) => Math.round((parseFloat(v) || 0) * 100)}
            onCommit={(cents) => patch({ base_price_cents: cents })}
            onError={(err) => showToast(err?.message || 'Save failed')}
          />
        )}
      </td>
      <td className="px-3 py-2 text-right">
        {isPerItem ? <span className="text-ink-3">—</span> : (
          <>
            <div className="text-[10px] text-ink-3">{includedLabel}</div>
            <EditableCell
              value={row[includedField] || ''}
              type="number"
              align="right"
              formatOnSave={(v) => parseInt(v, 10) || 0}
              onCommit={(n) => patch({ [includedField]: n })}
              onError={(err) => showToast(err?.message || 'Save failed')}
            />
          </>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        {isPerItem ? (
          <span className="text-ink-3">—</span>
        ) : (
          <span
            className="text-ink-3"
            title={`Set in Items → Extra lb (${row.name || row.key})`}
          >
            {row.extra_lb_cents != null ? `$${(row.extra_lb_cents / 100).toFixed(2)}` : '—'}
            <span className="ml-1 text-[9px] uppercase tracking-wider">via items</span>
          </span>
        )}
      </td>
      <td className="px-3 py-2">
        <EditableCell
          value={row.turnaround || ''}
          placeholder="in 24–48 hours"
          onCommit={(v) => patch({ turnaround: v })}
          onError={(err) => showToast(err?.message || 'Save failed')}
        />
      </td>
      <td className="px-3 py-2 text-center">
        <label className="inline-flex cursor-pointer items-center">
          <input
            type="checkbox"
            checked={!!row.active}
            onChange={(e) => patch({ active: e.target.checked }).catch(() => {})}
            className="h-4 w-4 rounded border-line"
          />
        </label>
      </td>
      <td className="px-3 py-2 text-center">
        <button
          type="button"
          onClick={handleDelete}
          aria-label={`Delete ${row.name}`}
          title="Delete service"
          className="text-ink-3 hover:text-red"
        >
          🗑
        </button>
      </td>
    </tr>
  )
}

// ══════════════════════════════════════════════════════════════════════
// Items
// ══════════════════════════════════════════════════════════════════════

function ItemsTable({ items, services, onChanged }) {
  async function addItem() {
    try { await insertItem(); onChanged() }
    catch (err) { showToast(err?.message || 'Add failed') }
  }

  // Group by category to render a header row per group.
  const grouped = useMemo(() => {
    const groups = []
    let curCat = null
    for (const it of items) {
      const cat = it.category || 'Other'
      if (cat !== curCat) { groups.push({ category: cat, items: [] }); curCat = cat }
      groups[groups.length - 1].items.push(it)
    }
    return groups
  }, [items])

  // Service key abbreviations for the checkbox columns.
  const serviceCols = services.map((s) => ({
    id: s.id,
    label: abbrev(s.key || s.name || ''),
    title: s.name || s.key,
  }))

  return (
    <section className="rounded-xl border border-line bg-white">
      <header className="flex items-center justify-between border-b border-line px-4 py-3">
        <h2 className="font-bricolage text-[15px] font-extrabold text-navy">Items</h2>
        <button
          type="button"
          onClick={addItem}
          className="rounded-lg bg-navy px-3 py-1.5 text-[11px] font-extrabold text-white"
        >
          + Add item
        </button>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead className="bg-[#F9FAFB] text-ink-2">
            <tr>
              <Th>Name</Th>
              <Th align="right">Price</Th>
              <Th>Type</Th>
              <Th>Unit</Th>
              <Th>Category</Th>
              {serviceCols.map((sc) => (
                <Th key={sc.id} align="center" title={sc.title}>{sc.label}</Th>
              ))}
              <Th align="center">Active</Th>
              <Th align="center"></Th>
            </tr>
          </thead>
          <tbody>
            {grouped.map((group) => (
              <>
                <tr key={`cat-${group.category}`} className="bg-[#F9FAFB]">
                  <td colSpan={5 + serviceCols.length + 2} className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-ink-2">
                    {group.category}
                  </td>
                </tr>
                {group.items.map((it) => (
                  <ItemRow key={it.id} row={it} serviceCols={serviceCols} onChanged={onChanged} />
                ))}
              </>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function ItemRow({ row, serviceCols, onChanged }) {
  const linkedSet = useMemo(() => new Set((row.item_services || []).map((x) => x.service_id)), [row])
  const isProtected = /^Extra lb \(/i.test(String(row.name || ''))

  async function patch(patch) {
    try { await patchItem(row.id, patch); onChanged() }
    catch (err) { showToast(err?.message || 'Save failed'); throw err }
  }

  async function toggleLink(serviceId, next) {
    try { await setItemServiceLink(row.id, serviceId, next); onChanged() }
    catch (err) { showToast(err?.message || 'Save failed') }
  }

  async function handleDelete() {
    if (isProtected) {
      showToast(
        `This item is used for automatic overage billing and can't be deleted. ` +
        `Deactivate it instead.`,
      )
      return
    }
    const msg =
      `Delete item "${row.name}"?\n\n` +
      `This can't be undone. Existing orders keep the item name and price ` +
      `on their invoice (item_id becomes null). ` +
      `If you just want to hide it from booking, use the Active toggle — that's reversible.`
    if (!window.confirm(msg)) return
    try {
      const res = await deleteItem(row.id, row.name)
      if (res.blocked && res.reason === 'protected') {
        showToast(
          `This item is used for automatic overage billing and can't be deleted. ` +
          `Deactivate it instead.`,
        )
        return
      }
      onChanged()
      showToast('Item deleted')
    } catch (err) {
      showToast(err?.message || 'Delete failed')
    }
  }

  return (
    <tr className="border-t border-line">
      <td className="px-3 py-2 font-bold text-navy">
        <EditableCell value={row.name || ''} onCommit={(v) => patch({ name: v })} onError={(err) => showToast(err?.message || 'Save failed')} />
      </td>
      <td className="px-3 py-2 text-right">
        <EditableCell
          value={row.price_cents != null ? (row.price_cents / 100).toFixed(2) : ''}
          display={row.price_cents != null ? `$${(row.price_cents / 100).toFixed(2)}` : <span className="text-ink-3">—</span>}
          type="number"
          align="right"
          formatOnSave={(v) => Math.round((parseFloat(v) || 0) * 100)}
          onCommit={(cents) => patch({ price_cents: cents })}
          onError={(err) => showToast(err?.message || 'Save failed')}
        />
      </td>
      <td className="px-3 py-2">
        <select
          value={row.kind || 'garment'}
          onChange={(e) => patch({ kind: e.target.value }).catch(() => {})}
          className="rounded border border-line bg-white px-1.5 py-1 text-[12px]"
        >
          {ITEM_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
      </td>
      <td className="px-3 py-2">
        <select
          value={row.unit || 'per_item'}
          onChange={(e) => patch({ unit: e.target.value }).catch(() => {})}
          className="rounded border border-line bg-white px-1.5 py-1 text-[12px]"
        >
          {ITEM_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
      </td>
      <td className="px-3 py-2">
        <EditableCell value={row.category || ''} onCommit={(v) => patch({ category: v })} onError={(err) => showToast(err?.message || 'Save failed')} />
      </td>
      {serviceCols.map((sc) => (
        <td key={sc.id} className="px-3 py-2 text-center">
          <input
            type="checkbox"
            checked={linkedSet.has(sc.id)}
            onChange={(e) => toggleLink(sc.id, e.target.checked)}
            className="h-4 w-4 rounded border-line"
            aria-label={`Link to ${sc.title}`}
          />
        </td>
      ))}
      <td className="px-3 py-2 text-center">
        <input
          type="checkbox"
          checked={!!row.active}
          onChange={(e) => patch({ active: e.target.checked }).catch(() => {})}
          className="h-4 w-4 rounded border-line"
        />
      </td>
      <td className="px-3 py-2 text-center">
        {isProtected ? (
          <span
            className="text-ink-3/60"
            title="Auto-added at weigh time. Deactivate instead of deleting."
            aria-label="Protected item"
          >
            🔒
          </span>
        ) : (
          <button
            type="button"
            onClick={handleDelete}
            aria-label={`Delete ${row.name}`}
            title="Delete item"
            className="text-ink-3 hover:text-red"
          >
            🗑
          </button>
        )}
      </td>
    </tr>
  )
}

function Th({ children, align, title }) {
  return (
    <th title={title} className={`px-3 py-2 text-${align || 'left'} font-bold uppercase tracking-wider`}>
      {children}
    </th>
  )
}

function abbrev(key) {
  const map = { wash_fold: 'WF', same_day: 'SD', wash_press: 'WP', members: 'MB', dry_cleaning: 'DC' }
  return map[key] || key.slice(0, 2).toUpperCase()
}
